import React, { useEffect, useState, useRef, useMemo, memo } from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import Router, { useRouter } from 'next/router';

// eslint-disable-next-line import/no-unresolved
import ProductCard from '@JohnLewisPartnership/jl-design-system/components/product-card/ProductCard';

import { ALPHA_ProductCardCarousel as ProductCardCarousel } from '@JohnLewisPartnership/jl-design-system/components/carousel/ALPHA/product-card-carousel/ProductCardCarousel';
import getProductUrl from '@JohnLewisPartnership/jl-design-system/utils/product/getProductUrl';
import { Message } from '@JohnLewisPartnership/jl-design-system/components/message/v2/Message';
import LoadingSpinner from '@JohnLewisPartnership/jl-design-system/components/loading-spinner/LoadingSpinner';
import Head from 'next/head';
import getConfig from 'next/config';
import { observe } from 'react-intersection-observer';
import {
  dispatchImmediateAnalyticsEvent,
  CHUNK_LOADED,
} from '../analytics/analyticsEvents';
import ProductCount from './product-count/ProductCount';
import StructuredData from '../structured-data/StructuredData';
import constants from '../../utils/constants';

import styles from './ProductGrid.module.scss';
import { mapProduct } from '../analytics/utils/mapProduct';
import { useProductCardSize } from '../../hooks/useProductCardSize';
import {
  useExperiencesContext,
  dispatchMonetateManagedImpressions,
} from '../experiences/Experiences';
import { useCompareContext } from '../../context/CompareContext';
import { useSearchContext } from '../../context/SearchContext';
import { useProductContext } from '../../context/ProductContext';
import { getProductChunks } from '../../api/products/product-chunks';
import { useRequestContext } from '../../context/RequestContext';
import { useDiscoveryContext } from '../../context/DiscoveryContext';
import { useEnergyDrawerData } from '../energy-drawer/useEnergyDrawerData';
import { useSocialProofContext } from '../../context/SocialProofContext';
import ProductCardContainer from './ProductCardContainer';

const Modal = dynamic(
  () =>
    import(
      /* webpackChunkName: "modal" */
      '@JohnLewisPartnership/jl-design-system/components/modal/Modal'
    ),
  {
    ssr: false,
  }
);

const { publicRuntimeConfig } = getConfig();

export const useProductGridData = () => {
  const { setProductContext, totalNumberOfResults, products } =
    useProductContext();

  const { products: discoveryRailProducts, title: discoveryRailTitle } =
    useDiscoveryContext();

  const { personalisedPlp } = useSearchContext();

  const pageCorrelationId = useRequestContext();

  const {
    experiences,
    isListViewOn,
    isPriceReductionFlagOn,
    isNowWasImpressionsFlagOn,
    monetateId,
  } = useExperiencesContext();

  const router = useRouter();

  const compare = useCompareContext();

  const socialProofingMessages = useSocialProofContext();

  const { isLargeCardSize } = useProductCardSize();

  const { openEnergyDrawer } = useEnergyDrawerData();

  const imageSize = isLargeCardSize
    ? '(max-width: 567px) calc(100vw - 32px), (min-width: 568px) and (max-width:767px) calc(50vw - 24px), (min-width: 768px) and (max-width:1200px) calc(25vw - 30), 282px'
    : '(max-width: 567px) calc(50vw - 24px), (min-width: 568px) and (max-width:767px) calc(33vw - 21px), (min-width: 768px) and (max-width:1200px) calc(19vw - 32), 205px';

  const imageWidths = ['117', '234', '330', '366', '411', '564', '686'];

  return {
    products,
    totalNumberOfResults,
    setProductContext,
    discoveryRailProducts,
    discoveryRailTitle,
    personalisedPlp,
    pageCorrelationId,
    socialProofingMessages,
    imageSize,
    imageWidths,
    router,
    compare,
    isLargeCardSize,
    experience: {
      experiences,
      isListViewOn,
      isPriceReductionFlagOn,
      isNowWasImpressionsFlagOn,
      monetateId,
    },
    openEnergyDrawer,
  };
};

export const ProductGrid = ({
  chunkSize = publicRuntimeConfig.chunkSize,
  fetchQuickViewData,
  handleDisplayM2MProductSelector,
  handleDisplayQuickAdd,
  pageSize = publicRuntimeConfig.pageSize,
  isLoadingChunk,
  isLoadingPage,
  setIsLoadingChunk,
  isChunkLoadError,
  setIsChunkLoadError,
}) => {
  const {
    setProductContext,
    totalNumberOfResults,
    products,
    discoveryRailProducts,
    personalisedPlp,
    discoveryRailTitle,
    pageCorrelationId,
    socialProofingMessages,
    isLargeCardSize,
    imageSize,
    imageWidths,
    experience,
    router,
    compare,
    openEnergyDrawer,
  } = useProductGridData();

  const {
    experiences,
    isListViewOn,
    isPriceReductionFlagOn,
    isNowWasImpressionsFlagOn,
    monetateId,
  } = experience;

  const {
    query: {
      chunk = 1,
      page = 1,
      facetId,
      'search-term': searchTerm,
      type,
      sortBy = '',
      listHead,
      lolcode,
      price,
      priceBands,
    },
  } = router;
  const pageRef = useRef(page);

  const THRESHOLD = 1800; // pixels

  const [chunkLoadingSentinelInView, setChunkLoadingSentinelInView] =
    useState(false);
  const [addToBasketError, setAddToBasketError] = useState(null);
  const [isMobileBreakpoint, setIsMobileBreakpoint] = useState(false);
  const [isBelowQuickViewBreakpoint, setIsBelowQuickViewBreakpoint] =
    useState(false);

  const productsToCurrentChunk = useMemo(() => {
    if (
      chunk == 8 ||
      (products.length > totalNumberOfResults &&
        totalNumberOfResults % chunkSize === 0)
    ) {
      return products;
    } else {
      return products.slice(0, chunk * chunkSize);
    }
  }, [products]);

  const clearAddToBasketError = () => {
    setAddToBasketError(null);
  };

  const handleAddToBasketClick = () => {
    return Promise.resolve();
  };

  const handleResize = () => {
    setIsMobileBreakpoint(window.innerWidth < constants.MOBILE_MAX_WIDTH);
    setIsBelowQuickViewBreakpoint(
      window.innerWidth < constants.QUICK_VIEW_MIN_WIDTH
    );
  };

  const setChunkNumber = (newChunkNumber) => {
    Router.replace(
      { query: { ...router.query, chunk: newChunkNumber } },
      null,
      {
        shallow: true,
        scroll: false,
      }
    );
  };
  const handleChunkLoadAnalytics = (chunkNum, chunkProducts) => {
    const numberOfProductsToCurrentChunk = (chunkNum - 1) * chunkSize;
    const numberOfAdditionalSponsoredProducts =
      products.length - numberOfProductsToCurrentChunk;

    dispatchImmediateAnalyticsEvent(CHUNK_LOADED, {
      chunkDetails: {
        chunkOffset: chunkNum - 1,
        products: chunkProducts.map((product, i) =>
          mapProduct(
            product,
            numberOfProductsToCurrentChunk +
              numberOfAdditionalSponsoredProducts +
              i
          )
        ),
        personalisedPlp,
      },
    });
  };

  useEffect(() => {
    let didCancel = false;

    if (page !== pageRef.current) {
      // There was a issue where the PLP was scrolling to the bottom, and not displaying all chunks,
      // when the back button was used to go from PLP to PLP.  This is because the browser scroll restore
      // happens before the product cards are rendered. This scroll is a lesser evil.(PLP-1059)
      window.scrollTo(0, 0);

      pageRef.current = page;
      // do not load chunks if this is running as a result of a page change (eg. back button)
      return;
    }

    if (
      chunk * chunkSize > products.length &&
      (page - 1) * pageSize + products.length < totalNumberOfResults
    ) {
      setIsLoadingChunk(true);
      getProductChunks(
        {
          page,
          chunk,
          term: searchTerm,
          type,
          facetId,
          sortBy,
          listHead,
          lolcode,
          price,
          priceBands,
        },
        pageCorrelationId
      )
        .then((products) => {
          setIsLoadingChunk(false);
          if (!didCancel) {
            setProductContext(products);
            handleChunkLoadAnalytics(chunk, products);
          }
        })
        .catch(() => {
          setIsLoadingChunk(false);
          setIsChunkLoadError(true);
          setChunkNumber(chunk - 1);
        });
    }

    return () => {
      didCancel = true;
    };
  }, [chunk, page]);

  useEffect(() => {
    const hasProductWithOffers = productsToCurrentChunk.find(
      (p) => p.promoMessages?.length
    );
    if (hasProductWithOffers) {
      dispatchMonetateManagedImpressions({
        monetateId,
        experiences,
        monetateFlagName: 'offersBadgeImpressionsEnabled',
      });
    }
  }, [products, chunk]);

  useEffect(() => {
    if (isNowWasImpressionsFlagOn) {
      const hasProductChangedPrice = productsToCurrentChunk.find(
        (p) => p.price?.was
      );
      if (hasProductChangedPrice) {
        dispatchMonetateManagedImpressions({
          monetateId,
          experiences,
          monetateFlagName: 'nowWasImpressionsEnabled',
        });
      }
    }
  }, [products, chunk]);

  useEffect(() => {
    if (personalisedPlp) {
      dispatchMonetateManagedImpressions({
        monetateId,
        experiences,
        monetateFlagName: 'discoImpressionsEnabled',
      });
    }
  }, [personalisedPlp]);

  useEffect(() => {
    let didCancel = false;

    if (
      chunkLoadingSentinelInView &&
      !isLoadingPage &&
      !isLoadingChunk &&
      !isChunkLoadError
    ) {
      const nextChunk = Number(chunk) + 1;

      if (
        // have reached the end of this page?
        nextChunk * chunkSize > pageSize ||
        // have we reached the end of the whole result set?
        (page - 1) * pageSize + chunk * chunkSize >= totalNumberOfResults
      ) {
        return;
      }

      if (!didCancel) {
        setChunkNumber(nextChunk);
      }
    }

    return () => {
      didCancel = true;
    };
  }, [chunkLoadingSentinelInView]);

  useEffect(() => {
    const footerEl = document.getElementById('footer-wrapper');

    setChunkLoadingSentinelInView(
      footerEl?.getBoundingClientRect().top < THRESHOLD
    );

    const destroyobserver = observe(footerEl, setChunkLoadingSentinelInView, {
      rootMargin: `${THRESHOLD}px`,
    });

    setIsMobileBreakpoint(window.innerWidth <= constants.MOBILE_MAX_WIDTH);
    setIsBelowQuickViewBreakpoint(
      window.innerWidth < constants.QUICK_VIEW_MIN_WIDTH
    );

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      destroyobserver();
    };
  }, []);

  const handleManualChunkLoad = () => {
    setIsChunkLoadError(false);
    setChunkNumber(Number(chunk) + 1);
  };

  const getImagePreload = (imageUrl, imageSize, imageWidths) => {
    const srcSetArr = imageWidths.map(
      (width) =>
        `${imageUrl.split('?')[0]}?wid=${width}&hei=${Math.round(
          width / 0.75
        )} ${width}w`
    );

    return (
      <link
        rel="preload"
        as="image"
        fetchpriority="high"
        href={srcSetArr[0].split(' ')[0]}
        imageSrcSet={srcSetArr.join(', ')}
        imageSizes={imageSize}
      />
    );
  };

  const AllProductCards = useMemo(() => {
    return productsToCurrentChunk.map((product, index) => {
      const { productId, image, type } = product;

      const pricePrefix =
        type === 'm2mRollerBlind' ? 'Blind price from' : undefined;

      const isLastProductOnPage = index + 1 === publicRuntimeConfig.pageSize;
      const isMultipleOfViewedProductFrequency = (index + 1) % chunkSize === 0;
      const shouldShowProductCount =
        !isLastProductOnPage && isMultipleOfViewedProductFrequency;

      const shouldShowDiscoveryRail =
        discoveryRailProducts?.length > 4 &&
        totalNumberOfResults >= chunkSize * 2 &&
        index === chunkSize - 5;

      return (
        <React.Fragment key={`${productId}`}>
          {index === 0 && (
            <Head>{getImagePreload(image, imageSize, imageWidths)}</Head>
          )}
          <ProductCardContainer
            product={product}
            index={index}
            isLargeCardSize={isLargeCardSize}
            isMobileBreakpoint={isMobileBreakpoint}
            isBelowQuickViewBreakpoint={isBelowQuickViewBreakpoint}
            openEnergyDrawer={openEnergyDrawer}
            handleAddToBasketClick={handleAddToBasketClick}
            handleDisplayQuickAdd={handleDisplayQuickAdd}
            fetchQuickViewData={fetchQuickViewData}
            imageSize={imageSize}
            imageWidths={imageWidths}
            handleDisplayM2MProductSelector={handleDisplayM2MProductSelector}
            socialProofingMessages={socialProofingMessages}
            pricePrefix={pricePrefix}
          />

          {shouldShowDiscoveryRail && (
            <aside
              data-test="discovery-rail"
              className={styles['discovery-rail']}
            >
              <ProductCardCarousel heading={discoveryRailTitle}>
                {discoveryRailProducts.slice(0, 10).map((railProduct, i) => (
                  <ProductCard
                    key={`discovery-rail-card-${i}`}
                    className={styles['discovery-rail__product']}
                    buttonSettings={{
                      hasCtaButton: false,
                      hasQuickViewButton: false,
                    }}
                    product={{
                      brand: railProduct.brand,
                      id: railProduct.id,
                      image: railProduct.image,
                      price: railProduct.price,
                      title: railProduct.title,
                      colorSwatches:
                        (railProduct.type !== 'productSet' &&
                          railProduct.colorSwatches) ||
                        null,
                    }}
                    hasBrandedTitle
                    imageSettings={{
                      loading: 'lazy',
                      sizes: imageSize,
                      srcSetWidths: imageWidths,
                    }}
                    pricePrefix={pricePrefix}
                    showPriceReduction={isPriceReductionFlagOn}
                  ></ProductCard>
                ))}
              </ProductCardCarousel>
            </aside>
          )}
          {shouldShowProductCount && (
            <div
              data-test="component-grid-column"
              className={classNames(styles['product-count'], {
                [styles['product-count--list-view']]: isListViewOn,
              })}
            >
              <ProductCount
                positionOnPage={index}
                isListViewOn={isListViewOn}
              />
            </div>
          )}
        </React.Fragment>
      );
    });
  }, [products, isLargeCardSize, compare, socialProofingMessages]);

  const structuredData = (
    <StructuredData>
      {() => ({
        '@type': 'ItemList',
        '@context': 'https://schema.org',
        numberOfItems: `(${products.length})`,
        itemListElement: products.map((product, i) => ({
          '@type': 'ListItem',
          url: `https://www.johnlewis.com${getProductUrl(
            product.title,
            product.id
          )}`,
          position: i + 1,
        })),
      })}
    </StructuredData>
  );

  return (
    <>
      <div
        data-test="component-grid-container"
        data-test-id="product-grid"
        data-monetate="disruptor-block-identifier"
        className={styles['product-grid']}
      >
        <meta name="total-number-of-items" content={products.length} />
        {structuredData}
        {AllProductCards}
      </div>
      {isLoadingChunk && (
        <div className={styles.chunkLoadingSpinner}>
          <LoadingSpinner />
        </div>
      )}
      {isChunkLoadError && (
        <Message
          type="error"
          title="There has been an error, please try again."
          buttonText="Load more products"
          buttonProps={{
            onClick: handleManualChunkLoad,
          }}
          className={styles.chunkLoadErrorMessage}
        />
      )}
      {addToBasketError && (
        <Modal
          isOpen
          header="Something went wrong"
          onClose={clearAddToBasketError}
        >
          <Message title={addToBasketError.message} type="error" />
        </Modal>
      )}
    </>
  );
};

const comparisonFn = (prevProps, nextProps) => {
  return nextProps.isLoadingPage === true;
};
export default memo(ProductGrid, comparisonFn);

ProductGrid.displayName = 'ProductGrid';

ProductGrid.defaultProps = {
  category: '',
  fetchQuickViewData: () => {},
  products: [],
  pageSize: 192,
  chunkSize: 24,
};

ProductGrid.propTypes = {
  category: PropTypes.string,
  fetchQuickViewData: PropTypes.func,
  products: PropTypes.instanceOf(Array),
  pageSize: PropTypes.number,
  chunkSize: PropTypes.number,
};
